/**
 * /admin/models/assignment — per-agent provider+model+temperature
 * defaults AND each model's live health, on one page. (Pre-#1281:
 * `/admin/agents/models`; tab label was "Models", now "Assignments" to
 * disambiguate from the renamed parent tab.)
 *
 * Extracted out of `/admin/config` (#429 Wave 3 Track D / #410) and
 * widened in the Models-UI pass to surface all SIX agents the runtime
 * has (planner, generator, healer, analyzer, auditor, scanner) plus a
 * temperature input per agent and a pricing-catalog-sourced model
 * dropdown instead of free-text provider+model inputs.
 *
 * **#1281 follow-up — config and health live together.** The first cut
 * of #1281 shipped model health on a *separate* "Catalog" page, so an
 * operator configuring an agent's model and an operator checking that
 * the model works were on two different screens. That split is gone:
 * each agent row now carries its own status (Working / Not working /
 * Checking…), a per-row "Check now" button, and the plain-language fix
 * message when the provider rejects it — right next to the controls
 * that set the model. The 20-minute scheduled sweep still updates these
 * statuses in the background; the "Check all now" banner kicks it early
 * after a key rotation. The embedding model — not an agent, so it has
 * no assignment controls — sits as a read-only row at the bottom so its
 * health is visible from the same screen.
 *
 * The original four-agent surface left scanner + analyzer invisible
 * (analyzer's runtime env-var name is the legacy `EXTRACTOR_*`, which
 * is why the resolver maps `extractor` → `analyzer` AppSettings key
 * but the env-var names stay put); operators had no admin path to
 * pin a model for those two. The widened surface fixes that.
 *
 * Why a pricing-catalog dropdown instead of free-text: the catalog
 * (`/api/admin/pricing`) is the single source of truth for which
 * model specs the system knows how to bill. Free-text inputs let an
 * operator pin a typo'd model that would crash agent runs at
 * parseModelSpec time (defense at the PUT validator catches that
 * today, but a dropdown removes the entire failure mode by making
 * typos impossible).
 *
 * Pre-fill semantics: each row's dropdown + temperature input start
 * pre-filled with the RESOLVED current values — what the agent is
 * actually using right now — so the operator can see what's running
 * even when no doc override exists. Pre-filled-but-unedited values
 * NEVER persist on save — only operator-edited fields go in the PUT
 * body. Where each value comes from is said in words under More details.
 *
 * An open agent reads in the order an operator acts on it: what is wrong
 * and what to do, the model, then any warning. Temperature, where each
 * setting comes from, the key and what the model can do sit under a closed
 * More details. Saving checks every agent whose model changed, inherited
 * ones included, one at a time.
 *
 * Recommendations come from OttoCloud, for its customers, and only when an
 * admin presses "Show recommendations" (#2891). Nothing is asked on open or
 * after a save; the review list applies picks through each agent's Save.
 *
 * Save is page-local: submitting writes ONLY the `agentModels` field
 * group, leaving every other AppSettings field group (features,
 * defaultRunBudgets, emailTemplates) untouched.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CopyIconButton } from '@/components/CopyIconButton'
import {
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Loader2,
  HelpCircle,
  RefreshCw,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  SearchableSelect,
  type SearchableSelectOption,
  type SearchableSelectFacet,
} from '@/components/ui'
import {
  adminAppSettingsAPI,
  adminModelVerificationAPI,
  type ModelCandidateCheck,
  adminModelRecommendationsAPI,
  type ModelRecommendationsResponse,
  adminPricingAPI,
  workspacesAPI,
  type AdminAgentName,
  type AgentModelDefaults,
  type AgentModelOverride,
  type AgentCapabilityDeclaration,
  type AgentModelBar,
  type AppSettingsResponse,
  type ModelPricingRow,
  type ModelSupplierStatus,
  type ModelTargetRow,
  type ModelVerificationState,
  type ResolvedAgentEntry,
  type ResolvedAppSettings,
  type RetiredModelSummary,
} from '@/lib/api'
import { AgentsSubNav } from '../AgentsSubNav'
import {
  overrideToNamePattern,
  parseNamePattern,
  modelPriceStatus,
  modelReadsImages,
  modelCostCategory,
  modelUnavailableReason,
  unavailableReasonText,
  supplierNameFor,
  HOSTED_SUPPLIER,
  optionFacets,
  barShortfalls,
  type BarShortfall,
  FACET_CURATED,
  FACET_FRONTIER,
  FACET_FITS_AGENT,
  ADMIN_AGENT_ORDER,
  AGENT_LABEL,
} from '@/lib/agentModelSpec'
import { CapabilityBadge, reasoningBadge, formatContextWindow, formatContextSize } from '@/lib/modelCapabilityBadges'
import { CostCategoryPill } from '@/components/CostCategoryPill'
import { AdvancedSettingsNotice } from '@/components/AdvancedSettingsNotice'
import { retiredByPattern, retiredOptionSuffix } from '@/lib/retiredModelLabel'
import { getActiveWorkspaceId } from '@/lib/workspace'
import {
  RecommendationsList,
  type ApplyResult,
  type RecommendationItem,
  type RecommendationsView,
} from './RecommendationsList'

const AGENTS = ADMIN_AGENT_ORDER

/** Facet chips on the model picker (#1844 Slice 1.5), in display order.
 * SearchableSelect appends its own "All Models" chip. Otto Curated is the
 * default view (see the call site's `defaultFacet`), so an operator opens on
 * a vetted shortlist instead of ~600 rows; the derived "Vision" facet reads
 * the #1842 `imageInput` flag (labeled "Vision" to match the capability
 * badge — #1854). */
/** Facet chips, in display order. `SearchableSelect` appends its own
 * "All Models" chip.
 *
 * The agent-aware chip comes FIRST, on every agent, but `Otto Curated` remains
 * the chip active on open (`defaultFacet` below) — the agent chip is
 * available, not imposed. Because the default view is not filtered by the bar,
 * the curated shortlist can still contain a model that misses it, so the
 * BADGES carry the signal and the assign-time warning is the backstop.
 *
 * The chips are SINGLE-select, which is why the bar is one agent-aware chip
 * rather than separate Tool calling / Reasoning / context chips: those could
 * each filter alone but never combine. */
function modelFacetsForAgent(
  agent: AdminAgentName,
  bar: AgentModelBar | undefined,
): readonly SearchableSelectFacet[] {
  // No bar (an older backend that does not serve one): no agent chip, rather
  // than a chip whose count would equal the whole catalog.
  const base: SearchableSelectFacet[] = []
  if (bar) {
    base.push({
      key: FACET_FITS_AGENT,
      label: `Fits the ${AGENT_LABEL[agent]}`,
      // States what the list IS (confirmed) and what it is not (a verdict on
      // everything else). A model we have no information about is left out of
      // this list but is neither excluded from use nor implied to be broken.
      hint: `Models we've confirmed meet what the ${AGENT_LABEL[agent].toLowerCase()} needs: ${describeBar(bar)}. Others are still available under All Models or by searching.`,
    })
  }
  return [
    ...base,
    { key: FACET_CURATED, label: 'Otto Curated' },
    { key: FACET_FRONTIER, label: 'Frontier' },
    // No Vision chip: whether vision matters is part of each agent's bar, so
    // "Fits the <Agent>" already answers it without asking the operator to.
  ]
}

/** A bar in plain words, e.g. "at least 200K of context, tool use, reasoning
 * and vision". Used by the chip's hint. */
function describeBar(bar: AgentModelBar): string {
  const parts = [`at least ${formatContextSize(bar.minContextTokens) ?? bar.minContextTokens} of context`]
  for (const need of bar.needs) parts.push(CRITERION_NOUN[need] ?? need)
  if (parts.length === 1) return parts[0]
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/** Plain nouns for each capability the bar can name. */
const CRITERION_NOUN: Record<string, string> = {
  toolCalling: 'tool use',
  reasoning: 'reasoning',
  imageInput: 'vision',
}

/** One warning line per shortfall, in the operator's words. Known misses say
 * what the model lacks and what the agent needs; unknowns say we don't know,
 * never that it fails. */
function shortfallLine(agent: AdminAgentName, s: BarShortfall, bar: AgentModelBar, contextTokens: number | undefined): string {
  const who = `the ${AGENT_LABEL[agent].toLowerCase()}`
  if (s.criterion === 'context') {
    const need = formatContextSize(bar.minContextTokens) ?? String(bar.minContextTokens)
    if (!s.known) return `We don't know how much context this model reads. ${capitalize(who)} needs at least ${need}.`
    const has = formatContextSize(contextTokens) ?? String(contextTokens)
    return `This model reads up to ${has} of context. ${capitalize(who)} needs at least ${need}.`
  }
  if (s.criterion === 'toolCalling') {
    return s.known
      ? `This model can't use tools, and ${who} needs that to ${AGENT_PURPOSE[agent]}.`
      : `We don't know if this model can use tools, which ${who} needs.`
  }
  if (s.criterion === 'reasoning') {
    return s.known
      ? `This model doesn't reason, and ${who} works better with a model that does.`
      : `We don't know if this model reasons, which ${who} works better with.`
  }
  if (s.criterion === 'imageInput') {
    return s.known
      ? `This model can't read images, and ${who} takes screenshots while it explores your app.`
      : `We don't know if this model can read images, which ${who} uses for screenshots.`
  }
  return `We can't check whether this model has everything ${who} needs.`
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** What each agent uses tools FOR, in the operator's words — the second half of
 * the warning's first sentence ("…the healer needs that to fix a test").
 *
 * Per agent rather than one generic "to do its job" because the mockup's
 * wording is binding and it is specific. It also earns its keep: an operator
 * who does not know what the planner is learns it from the warning, at the
 * moment they need to decide something about it.
 *
 * Only the three gated agents can produce this warning; the rest are listed so
 * the record stays exhaustive if a requirement is ever added to one. */
const AGENT_PURPOSE: Record<AdminAgentName, string> = {
  planner: 'explore your app',
  generator: 'write a test',
  healer: 'fix a test',
  analyzer: 'do its job',
  auditor: 'do its job',
  scanner: 'do its job',
  proposer: 'do its job',
  refiner: 'do its job',
  dedupJudge: 'do its job',
}


/** The verification target id for an agent row. The backend keys agent
 * targets as `agent:<adminName>` (and uses the admin label `analyzer`,
 * NOT the runtime `extractor`, as the segment) — so the join from a
 * config row to its health is just this prefix, no extractor special-
 * casing on the frontend. See `application/modelVerification/targets.ts`. */
function agentTargetId(agent: AdminAgentName): string {
  return `agent:${agent}`
}

/** Sentinel value the dropdown's first option carries — selecting it
 * clears any doc override for the row and falls through to the env
 * cascade. Empty string lets us bind directly to the `<select>` value
 * without a separate "is-cleared" boolean. */
const CLEAR_OPTION_VALUE = ''

/** Human "N minutes ago" for the last-checked timestamp. Falls back to
 * the absolute date when older than a day. Kept inline so the page
 * renders the right phrasing without a heavier date library in the
 * bundle. */
function relativeTime(iso: string | null): string {
  if (!iso) return ''
  const ts = new Date(iso).getTime()
  if (Number.isNaN(ts)) return ''
  // Clamp to 0 so a future timestamp (client clock behind the server)
  // reads "a moment ago" rather than a misleading large/negative value.
  const seconds = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (seconds < 60) return 'a moment ago'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return new Date(iso).toLocaleDateString()
}

function StatusPill({ state }: { state: ModelVerificationState }) {
  switch (state) {
    case 'working':
      return (
        <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Verified
        </Badge>
      )
    case 'notWorking':
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Not working
        </Badge>
      )
    case 'checking':
      return (
        <Badge variant="outline" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Checking…
        </Badge>
      )
    case 'notChecked':
      return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
          <HelpCircle className="h-3 w-3" />
          Not checked yet
        </Badge>
      )
  }
}

/** The verification state a row ACTUALLY renders, after the #1364
 * cache-invalidation downgrades. A saved `working`/`notWorking` is only
 * trustworthy when (a) there is a live API key to call the model with and
 * (b) the model selection has not changed since the check ran — otherwise it
 * is downgraded to `notChecked` so the operator re-verifies against the
 * current configuration instead of trusting a stale badge. While a check is
 * in flight the state is `checking`.
 *
 * This is the ONE definition of that rule: the per-row health block AND the
 * page banner both read health through it, so they can never disagree. The
 * banner used to key off the raw `status.state` and promise a fix note for
 * rows this helper had downgraded — so no note rendered (#2090). The server
 * mirrors this same rule when it computes the shared `anyNotWorking` boolean
 * (see routes/admin/modelVerification.ts); keep the two in sync. */
export function effectiveModelState(
  target: ModelTargetRow,
  checking = false,
): ModelVerificationState {
  if (checking) return 'checking'
  const cached = target.status?.state ?? 'notChecked'
  // A served-locally model (the bundled local embeddings) needs no key, so a
  // null preview is not "unconfigured" for it and must not downgrade the badge.
  const noLiveKey = target.apiKeyPreview === null && !target.servedLocally
  const modelChanged =
    target.status !== null &&
    (target.status.checkedModelId !== target.model ||
      (target.status.checkedProvider ?? null) !== (target.provider ?? null))
  return noLiveKey || modelChanged ? 'notChecked' : cached
}

/**
 * Whether a target will ACTUALLY render a fix note: the effective state above
 * PLUS the note's own `fixMessage` guard. Keying off the raw `status.state`
 * lit the banner for downgraded rows that render no note (#2090).
 *
 * Extracted in #2112 because a third reader appeared. The agent rows collapse,
 * and a failing agent's row opens itself so the banner's promise — "See the fix
 * note under each row" — stays true. If the rows judged failure by a different
 * rule than the banner, we would be back to a banner pointing at a note that
 * is not there, only with the row closed instead of the note missing.
 */
export function isNotWorkingWithFix(target: ModelTargetRow, checking = false): boolean {
  return effectiveModelState(target, checking) === 'notWorking' && !!target.status?.fixMessage
}

/** The inline health block that hangs off each agent row (and the
 * embedding row): the current status pill, last-checked stamp, a
 * per-row "Check now" button, and — when failing — the plain-language
 * fix message naming the env var to look at. The verify target may be
 * absent (verification list still loading or soft-failed); in that case
 * we render nothing rather than a misleading "Not checked yet". */
function ModelHealth({
  target,
  checking,
  error,
  onCheck,
  unsavedModel = false,
}: {
  target: ModelTargetRow | undefined
  checking: boolean
  error: string | undefined
  onCheck: () => void
  /** The picker holds a model that is not saved yet. Health, and what Check
   * now checks, belong to the SAVED model, so until the save the row says
   * the new model is checked then, and Check now waits. */
  unsavedModel?: boolean
}) {
  if (!target) return null
  if (unsavedModel) {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="health-unsaved">
        <StatusPill state="notChecked" />
        <span className="text-xs text-muted-foreground">The new model is checked when you press Save.</span>
        <Button type="button" size="sm" variant="outline" className="ml-auto" disabled>
          Check now
        </Button>
      </div>
    )
  }
  // The rendered state is the shared #1364 downgrade — see effectiveModelState,
  // which the page banner reads too so they never disagree (#2090). The two
  // locals below re-derive WHY a downgrade happened, only to choose the hint
  // text; both stay suppressed while a check is in flight (the cached
  // checkedProvider / checkedModelId can be stale mid-check, which would wrongly
  // fire the "model changed" hint over the spinner — Copilot on PR #1365).
  const cachedState = target.status?.state ?? 'notChecked'
  const state = effectiveModelState(target, checking)
  const noLiveKey = !checking && target.apiKeyPreview === null && !target.servedLocally
  const modelChangedSinceCheck =
    !checking &&
    target.status !== null &&
    (target.status.checkedModelId !== target.model ||
      (target.status.checkedProvider ?? null) !== (target.provider ?? null))
  const statusRow = (
    <div className="flex items-center gap-2 flex-wrap">
      <StatusPill state={state} />
      {/* The "checked X ago" stamp only makes sense when the pill is
          showing the cached state we got from Mongo. If we downgraded
          to "Not checked yet" (no live key or the operator changed
          the model since the check) the stamp would point at a check
          that's no longer relevant; suppress it. */}
      {target.status?.lastCheckedAt
        && state !== 'checking'
        && state === cachedState && (
          <span className="text-xs text-muted-foreground">
            checked {relativeTime(target.status.lastCheckedAt)}
          </span>
        )}
      {modelChangedSinceCheck && !noLiveKey && (
        <span className="text-xs text-muted-foreground">
          The model changed since its last check.
        </span>
      )}
      {noLiveKey && (
        <span className="text-xs text-muted-foreground">
          No key is set for this model's supplier.
        </span>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="ml-auto"
        onClick={onCheck}
        disabled={checking}
      >
        {checking ? 'Checking…' : 'Check now'}
      </Button>
    </div>
  )
  // A failing model shows its reason in the same box as its status, so the
  // first thing in an open row says what is wrong and what to do.
  if (state === 'notWorking' && target.status?.fixMessage) {
    return (
      <div
        className="rounded-md border border-destructive/40 bg-destructive/10 p-3 space-y-1.5"
        data-testid="health-problem"
      >
        {statusRow}
        <p className="text-sm font-medium text-destructive">
          {target.status.fixMessage}
        </p>
        {target.status.errorMessage && (
          <p className="text-xs text-muted-foreground">
            The supplier said: <span className="font-mono">{target.status.errorMessage}</span>
          </p>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {statusRow}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/** The masked API-key line for a row — "API key: sk-a…Z9k2 (OPENAI_API_KEY)".
 * Lets an operator confirm WHICH key is configured (e.g. after a
 * rotation) without exposing the secret; the preview is masked server-
 * side and the full value never reaches the browser. Shows "not set"
 * when no key in the chain is configured. */
function ApiKeyPreview({ target }: { target: ModelTargetRow | undefined }) {
  if (!target) return null
  return (
    <p className="text-xs text-muted-foreground">
      <span className="font-medium text-foreground/70">API key:</span>{' '}
      {target.apiKeyPreview ? (
        <>
          <span className="font-mono">{target.apiKeyPreview}</span>
          {/* Name the env var the preview was ACTUALLY read from — not a
              fixed chain[0], which would misattribute a fallback var
              during a rotation. */}
          {target.apiKeyEnvUsed && (
            <span className="text-muted-foreground/70"> ({target.apiKeyEnvUsed})</span>
          )}
        </>
      ) : target.servedLocally ? (
        // The local bundled model runs on the machine and needs no key, so
        // show that rather than "not set", which reads as a misconfiguration.
        <span className="italic">not needed (served locally)</span>
      ) : (
        <span className="italic">not set</span>
      )}
    </p>
  )
}

/** One line of an env-var fallback chain — "API key: FOO → BAR".
 * Rendered on the read-only embedding row, where there are no edit
 * controls and the chain is the only way to see which environment
 * variables back the model. */
function EnvChainLine({ label, chain }: { label: string; chain: readonly string[] }) {
  return (
    <div>
      <span className="font-medium text-foreground/70">{label}:</span>{' '}
      {chain.length > 0
        ? chain.map((n, i) => (
            <span key={n} className="font-mono">
              {n}
              {i < chain.length - 1 ? ' → ' : ''}
            </span>
          ))
        : '(none)'}
    </div>
  )
}

/** Where an agent's model comes from, in the words More details uses.
 * `per-run` never reaches this page; it reads as the page's own source so
 * the switch stays exhaustive. */
function modelSourceWords(source: ResolvedAgentEntry['model']['source']): string {
  switch (source) {
    case 'app-settings':
    case 'per-run':
      return 'picked on this page'
    case 'per-agent-env':
    case 'default-env':
    case 'legacy-env':
      return "from the install's settings file"
    case 'hardcoded':
      return "OttoTester's built-in default"
  }
}

/** Where an agent's temperature comes from, in the same words. */
function temperatureSourceWords(source: ResolvedAgentEntry['temperature']['source']): string {
  switch (source) {
    case 'app-settings':
    case 'per-run':
      return 'set on this page'
    case 'per-agent-env':
    case 'default-env':
      return "from the install's settings file"
    case 'unset':
      return "the supplier's default"
  }
}

/** What an agent's last Save found (#2890), shown under its picker. */
interface SaveResult {
  tone: 'ok' | 'bad' | 'warn' | 'info'
  text: string
  /** Set when the model was written, whatever the check found. The
   * recommendations list reads it to relabel a saved agent's line, rather
   * than guessing from the tone, which only picks the color. */
  saved?: boolean
  /** Set when the model was checked and works, so the recommendations list
   * can say so plainly (#2891). */
  checkedWorking?: boolean
}

const SAVE_RESULT_STYLE: Record<SaveResult['tone'], string> = {
  ok: 'bg-success/10 text-success',
  bad: 'bg-destructive/10 text-destructive',
  warn: 'bg-warning/10 text-warning',
  info: 'bg-muted text-muted-foreground',
}

interface RowDraft {
  /** `<provider>:<model>` namePattern, or `''` for "cleared / cascade". */
  namePattern: string
  /** Raw text in the temperature input. String so empty (`''`) is
   * distinguishable from `0` and partial input (`0.`) doesn't NaN-coerce
   * mid-keystroke. */
  temperature: string
  /** True when the operator has edited EITHER field. Until set, the
   * row is treated as pre-filled-but-untouched and excluded from the
   * PUT body. */
  dirty: boolean
}

export function AgentModelsPage() {
  const [data, setData] = useState<AppSettingsResponse | null>(null)
  const [pricingRows, setPricingRows] = useState<ModelPricingRow[] | null>(null)
  // #2014 — retired models are absent from `pricingRows`, but an agent may
  // still be assigned one; the picker needs them to explain the current value.
  const [retiredModels, setRetiredModels] = useState<readonly RetiredModelSummary[]>([])
  // Each supplier and whether this install can use it. Drives the "Where
  // models come from" card and explains a model the picker no longer offers.
  const [suppliers, setSuppliers] = useState<readonly ModelSupplierStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Verification health — loaded alongside config but kept on its own
  // state so a verification fetch failure NEVER blocks the assignment
  // form. Health is supporting context; configuring a model must work
  // even when the verification service is down.
  const [targets, setTargets] = useState<ModelTargetRow[] | null>(null)
  const [perRowChecking, setPerRowChecking] = useState<Record<string, boolean>>({})
  const [perRowError, setPerRowError] = useState<Record<string, string>>({})
  const [checkAllRunning, setCheckAllRunning] = useState(false)
  const [checkAllMessage, setCheckAllMessage] = useState<string | null>(null)

  /** Loads the page's settings and catalog. Returns the settings read, or
   * null when it failed, so a save can compare models before and after. */
  async function reload(): Promise<AppSettingsResponse | null> {
    setLoading(true)
    try {
      const [settings, pricing] = await Promise.all([
        adminAppSettingsAPI.get(),
        adminPricingAPI.list(),
      ])
      setData(settings)
      setPricingRows(pricing.rows)
      setRetiredModels(pricing.retired)
      setSuppliers(pricing.suppliers ?? [])
      setError(null)
      return settings
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load agent models')
      return null
    } finally {
      setLoading(false)
    }
  }

  async function reloadVerification(): Promise<void> {
    try {
      const result = await adminModelVerificationAPI.list()
      setTargets(result.targets)
    } catch {
      // Soft signal — leave the health blocks hidden rather than throwing
      // an error toast over the config form.
    }
  }

  useEffect(() => {
    void reload()
    void reloadVerification()
  }, [])

  async function checkRow(targetId: string): Promise<void> {
    setPerRowChecking((s) => ({ ...s, [targetId]: true }))
    setPerRowError((s) => ({ ...s, [targetId]: '' }))
    try {
      const result = await adminModelVerificationAPI.verify(targetId)
      // Splice just the freshly-checked target back in — keeps the
      // in-flight indicator honest for the other rows.
      setTargets((rows) =>
        rows ? rows.map((row) => (row.targetId === targetId ? result.target : row)) : rows,
      )
    } catch (e) {
      setPerRowError((s) => ({
        ...s,
        [targetId]: e instanceof Error ? e.message : 'Check failed',
      }))
    } finally {
      setPerRowChecking((s) => ({ ...s, [targetId]: false }))
    }
  }

  /** After a save, checks every agent whose model changed, one at a time.
   *
   * Compares each agent's effective model before and after the save, so an
   * agent that only inherits a changed model (the Deduper runs the Planner's,
   * the Refiner the Proposer's) is checked too, and a temperature-only edit
   * checks nothing: the check never sends a temperature. One at a time, like
   * the scheduled check, so a low supplier quota isn't tripped. `checkRow`
   * keeps a failure on its own agent, so the rest still run, and the save
   * stands whatever the checks find. */
  function checkChangedModels(
    before: Partial<Record<AdminAgentName, string>>,
    after: AppSettingsResponse,
  ): Promise<void> {
    const changed = AGENTS.filter((a) => {
      const was = before[a]
      const now = after.resolved.agentModels[a]?.model.spec
      return was !== undefined && now !== undefined && was !== now
    })
    if (changed.length === 0) return checkQueue.current
    // Joins the end of one page-wide queue, so a second save while these
    // run never checks alongside them: that would defeat the one-at-a-time
    // rule, and an older answer could land after a newer one.
    checkQueue.current = checkQueue.current.then(async () => {
      // The health rows still describe the old models until this reloads.
      await reloadVerification()
      for (const a of changed) await checkRow(agentTargetId(a))
    })
    return checkQueue.current
  }
  /** The checks saves have started, in order. `checkRow` never throws, so
   * one failed check never stops the queue. */
  const checkQueue = useRef<Promise<void>>(Promise.resolve())

  async function checkAll(): Promise<void> {
    setCheckAllRunning(true)
    setCheckAllMessage(null)
    try {
      await adminModelVerificationAPI.runAllNow()
      setCheckAllMessage('Check started. Statuses below will refresh shortly.')
      // The scheduled-job run executes asynchronously; poll a few times
      // so the page catches up to the new statuses without a manual
      // reload.
      for (let i = 0; i < 6; i++) {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        await reloadVerification()
      }
    } catch (e) {
      setCheckAllMessage(
        e instanceof Error
          ? `Couldn't start the check: ${e.message}`
          : "Couldn't start the check",
      )
    } finally {
      setCheckAllRunning(false)
    }
  }

  if (loading && !data) {
    return (
      <div className="space-y-6 max-w-3xl">
        <AgentsSubNav />
        <p className="text-sm text-muted-foreground">Loading agent models…</p>
      </div>
    )
  }
  if (error && !data) {
    return (
      <div className="space-y-6 max-w-3xl">
        <AgentsSubNav />
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      </div>
    )
  }
  if (!data || !pricingRows) return null

  const statusByTarget = new Map((targets ?? []).map((t) => [t.targetId, t]))
  const embeddingTarget = targets?.find((t) => t.targetKind === 'embedding')
  // Fire the red banner only when a row will ACTUALLY render its fix note — the
  // same effective state (including the row's in-flight `checking`) plus the
  // note's own `fixMessage` guard. Keying off raw `status.state` used to light
  // the banner for downgraded rows that show no note (#2090).
  const anyNotWorking = (targets ?? []).some((t) =>
    isNotWorkingWithFix(t, perRowChecking[t.targetId] ?? false),
  )

  return (
    <div className="space-y-6 max-w-3xl">
      <AgentsSubNav />
      <div>
        <h1 className="text-2xl font-semibold">Per-agent model assignment</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pick each agent's model and press its Save. Save checks the model first.
        </p>
      </div>

      <AdvancedSettingsNotice />

      {/* Health banner — "Check all now" kicks the 20-minute scheduled
          sweep early (same path as the next auto-tick). The "Change
          schedule" link points at the scheduled-jobs page for this
          check. Only rendered once the verification list has loaded. */}
      {targets && (
        <Card>
          <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              {anyNotWorking ? (
                <span className="text-destructive font-medium">
                  One or more models are not working. See the fix note under each row.
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Models are checked automatically on a schedule. Use “Check now” after a key change.
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => void checkAll()}
                disabled={checkAllRunning}
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${checkAllRunning ? 'animate-spin' : ''}`} />
                {checkAllRunning ? 'Checking all…' : 'Check all now'}
              </Button>
              <Link
                to="/admin/scheduled-jobs/verifyAllModels"
                className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                title="Open the scheduled-jobs page for this check"
              >
                Change schedule
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
      {checkAllMessage && (
        <p className="text-sm text-muted-foreground" role="status">
          {checkAllMessage}
        </p>
      )}

      {/* Which suppliers are set up lives on the Model catalog, beside the
          catalog it describes; this page only says why the pickers below
          are limited, and links there. */}
      {suppliers.length > 0 && (
        <p className="text-sm text-muted-foreground" data-testid="model-sources-link">
          Only models your set-up suppliers offer can be picked.{' '}
          <Link to="/admin/models/catalog" className="font-medium text-primary underline-offset-4 hover:underline">
            See where models come from
          </Link>
        </p>
      )}

      <AgentModelsSection
        doc={data.agentModels}
        resolved={data.resolved}
        capabilityRequirements={data.agentCapabilityRequirements}
        originPolicyActive={data.originPolicyActive === true}
        pricingRows={pricingRows}
                    retiredModels={retiredModels}
        suppliers={suppliers}
        statusByTarget={statusByTarget}
        perRowChecking={perRowChecking}
        perRowError={perRowError}
        onCheckRow={checkRow}
        onSaved={async (before, { checkChanged }) => {
          const fresh = await reload()
          // Not awaited: the save is done, and the checks report on each
          // agent as they finish.
          if (fresh && checkChanged) void checkChangedModels(before, fresh)
          // Unchecked: still refresh the health rows, without a probe, so the
          // new model is not shown with the old model's result. It then reads
          // "Not checked yet", since its last check was of another model.
          else if (fresh) void reloadVerification()
        }}
      />

      {embeddingTarget && (
        <EmbeddingModelCard
          target={embeddingTarget}
          checking={perRowChecking[embeddingTarget.targetId] ?? false}
          error={perRowError[embeddingTarget.targetId]}
          onCheck={() => void checkRow(embeddingTarget.targetId)}
        />
      )}
    </div>
  )
}

/** Read-only health row for the embedding model. The embedding model
 * isn't an agent, so it has no assignment controls — but an operator
 * watching for a stale embedding key wants to see its health from the
 * same screen. Shows the resolved model + env-var chains + status, with
 * the same per-row "Check now" affordance as the agent rows. */
function EmbeddingModelCard({
  target,
  checking,
  error,
  onCheck,
}: {
  target: ModelTargetRow
  checking: boolean
  error: string | undefined
  onCheck: () => void
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{target.displayName}</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          <span className="font-mono">{target.provider}</span>
          {' · '}
          <span className="font-mono">{target.model}</span>
          {target.expectedDimensions !== undefined && (
            <>
              {' · '}
              <span>{target.expectedDimensions} dimensions</span>
            </>
          )}
          {' · read-only (set by environment, not editable here)'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ModelHealth target={target} checking={checking} error={error} onCheck={onCheck} />
        <ApiKeyPreview target={target} />
        <div className="text-xs text-muted-foreground space-y-1">
          <EnvChainLine label="API key env" chain={target.apiKeyEnvChain} />
          <EnvChainLine label="Base URL" chain={target.baseUrlEnvChain} />
          <EnvChainLine label="Model" chain={target.modelEnvChain} />
        </div>
      </CardContent>
    </Card>
  )
}

/** The plain-language "why this pick" tooltip from a ranked model's numbers.
 * The ranking library returns numbers only; formatting lives here so the
 * library stays presentation-neutral (#1844 Slice 3). Leads with the honest
 * anchor (the model's real average) so a percent-looking scaled score never
 * misreads as an absolute rating. */
/**
 * The organization the admin is working in, for recommendations on our
 * hosted install, where each organization has its own OttoCloud account
 * (#2891). `/admin` sits outside the workspace provider, since a superadmin
 * can open it with no workspace picked, so this asks for the workspace this
 * tab last used, else the user's default one, and takes its organization.
 * Undefined when neither can be read; a hosted install then asks nothing.
 * Other installs ignore it.
 */
async function currentOrgId(): Promise<string | undefined> {
  try {
    const id = getActiveWorkspaceId()
    const workspace = id ? await workspacesAPI.getById(id) : await workspacesAPI.getMe()
    return workspace.orgId || undefined
  } catch {
    return undefined
  }
}

/** Whether two model specs name the same model. A bare id is an OpenAI
 * model, as the server resolves it, while a recommendation always names
 * its supplier, so `gpt-5.4-mini` and `openai:gpt-5.4-mini` match. */
function sameModel(a: string, b: string): boolean {
  const full = (s: string) => (parseNamePattern(s).provider ? s : `openai:${s}`)
  return full(a) === full(b)
}

/**
 * The always-visible summary line for one agent (#2112).
 *
 * Seven agents each ran about 570px tall, separated by a 1px line and 16px of
 * space — the same weight as the three bordered boxes INSIDE each one, on the
 * same white ground, with the agent's name set in the very `Label` style used
 * by the fields beneath it. Nothing told the eye which line was the boundary,
 * so it was easy to edit the wrong agent's model. This header is that missing
 * boundary: a filled strip, the name at heading size, and — since the body
 * below it is now hidden until asked for — enough of a summary to answer
 * "what is this agent set to?" without opening anything.
 *
 * Every value here is read through the SAME helper the open body uses:
 * `effectiveModelState` + `StatusPill` for health, `modelCostCategory` +
 * `CostCategoryPill` for cost, the draft's own `dirty` flag for unsaved edits.
 * A summary that re-derived any of those could disagree with the row it
 * summarises, which is worse than not summarising at all.
 *
 * Absent target renders no status, exactly as `ModelHealth` does: the
 * verification list may still be loading, or its fetch may have soft-failed
 * (see `reloadVerification`), and neither is "not checked yet". Inventing a
 * header-only "status unavailable" would make this the one component with a
 * fifth health state.
 */
function AgentRowHeader({
  agent,
  open,
  bodyId,
  modelSpec,
  costCategory,
  unavailable,
  dirty,
  unsavedModel,
  target,
  checking,
  onToggle,
}: {
  agent: AdminAgentName
  open: boolean
  bodyId: string
  modelSpec: string
  costCategory: ReturnType<typeof modelCostCategory>
  /** The model can't be used on this install; says so in the header so a
   * closed row still shows it. */
  unavailable: boolean
  dirty: boolean
  /** The draft model differs from the saved one. The saved model's health
   * would describe the wrong model, so the status is left off until saved. */
  unsavedModel: boolean
  target: ModelTargetRow | undefined
  checking: boolean
  onToggle: () => void
}) {
  return (
    <button
      // type="button" is load-bearing: this sits inside the section's <form>,
      // and a default-type button submits it — so opening a row would have
      // saved the page.
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      // Only while the body is actually mounted: `aria-controls` pointing at an
      // id that is not in the document is not conformant, and the body is
      // unmounted when the row is closed. `aria-expanded` carries the state on
      // its own either way.
      aria-controls={open ? bodyId : undefined}
      className={`w-full flex items-center gap-x-3 gap-y-1.5 flex-wrap px-3 py-2.5 text-left transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ring-inset
        ${open ? 'bg-muted' : 'bg-muted/60 hover:bg-muted'}`}
    >
      <ChevronRight
        aria-hidden="true"
        className={`h-4 w-4 flex-none text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
      />
      <span className="text-base font-semibold">{AGENT_LABEL[agent]}</span>
      {/* The bare spaces between these children are not decoration. A button's
          accessible name is its text content concatenated, and flex drops
          whitespace-only children from the layout — so without them a screen
          reader reads "plannergpt-5.4-miniTemperature", while the screen looks
          identical either way.

          min-w-0 is what lets the model spec truncate instead of pushing the
          badges off the edge — the content column is max-w-3xl (768px), which
          a long provider:model spec plus every badge will exceed. */}
      {' '}
      <span className="ml-auto flex min-w-0 items-center gap-2 flex-wrap justify-end text-xs text-muted-foreground">
        {dirty && (
          <Badge variant="warning" className="text-[10px]">Not saved yet</Badge>
        )}
        {' '}
        <span className="font-mono text-xs text-foreground truncate max-w-full">{modelSpec}</span>
        {' '}
        {costCategory && <CostCategoryPill category={costCategory} />}
        {' '}
        {unavailable ? (
          <Badge variant="destructive" className="text-[10px]">No longer available</Badge>
        ) : (
          target && !unsavedModel && <StatusPill state={effectiveModelState(target, checking)} />
        )}
      </span>
    </button>
  )
}

/**
 * A row for an agent this backend has not returned a model for.
 *
 * Shown disabled rather than dropped. The condition is a deployment window —
 * the browser holds a newer bundle than the server it is talking to, so an
 * agent the bundle knows about (the refiner, #2132) is missing from the
 * response. Hiding the row would read as the agent having been deleted;
 * showing it greyed out says what is actually true, which is that it is not
 * ready yet. Not a `<button>`: there is nothing to open, and a disabled
 * control the operator can tab to but not use is worse than plain text.
 */
function UnavailableAgentRow({ agent }: { agent: AdminAgentName }) {
  return (
    <div className="w-full flex items-center gap-x-3 gap-y-1.5 flex-wrap px-3 py-2.5 text-left bg-muted/30">
      {/* Holds the chevron's width so the agent names stay in one column with
          the rows above and below. */}
      <span aria-hidden="true" className="h-4 w-4 flex-none" />
      <span className="text-base font-semibold text-muted-foreground">{AGENT_LABEL[agent]}</span>
      {' '}
      <span className="ml-auto text-xs text-muted-foreground">
        Not available yet. It will appear once the update finishes.
      </span>
    </div>
  )
}

function AgentModelsSection({
  doc,
  resolved,
  capabilityRequirements,
  originPolicyActive,
  pricingRows,
  retiredModels,
  suppliers,
  statusByTarget,
  perRowChecking,
  perRowError,
  onCheckRow,
  onSaved,
}: {
  doc: AgentModelDefaults | undefined
  resolved: ResolvedAppSettings
  capabilityRequirements: Record<string, AgentCapabilityDeclaration> | undefined
  /** #2107 — this deployment keeps excluded models out of the catalog. A model
   * that is not in the catalog cannot be used, so the picker stops offering
   * one it would normally keep visible, and a row still pointing at one
   * refuses to save. */
  originPolicyActive: boolean
  pricingRows: readonly ModelPricingRow[]
  /** Models the catalog no longer offers (#2014). Not in `pricingRows` — an
   * operator cannot pick one — but an agent may already be assigned one, and
   * the picker has to say which of the two things happened to it. */
  retiredModels: readonly RetiredModelSummary[]
  /** Each supplier's state, to explain a saved model that is no longer
   * available and to say which suppliers the picker leaves out. */
  suppliers: readonly ModelSupplierStatus[]
  statusByTarget: ReadonlyMap<string, ModelTargetRow>
  perRowChecking: Record<string, boolean>
  perRowError: Record<string, string>
  onCheckRow: (targetId: string) => void
  /** Called after a successful save with each agent's effective model from
   * before it, so the page can check the ones that changed. `checkChanged`
   * is false when the supplier could not be reached, so the agent is left
   * "Not checked yet" rather than checked again and marked broken. */
  onSaved: (before: Partial<Record<AdminAgentName, string>>, opts: { checkChanged: boolean }) => Promise<void>
}) {
  /** The agents the server actually sent a resolved entry for.
   *
   * Normally that is every name in `AGENTS`. It is NOT during a deployment: the
   * browser can hold a newer bundle than the backend it is talking to, so an
   * agent added on the frontend (the refiner, #2132) is in `AGENTS` while the
   * server has never heard of it. `resolved.agentModels[a]` is then undefined,
   * and reading `r.model.spec` off it throws and blanks the entire page rather
   * than the one row that cannot be drawn.
   *
   * This is the list of rows that can be EDITED — what the draft is seeded
   * from and what a save walks. The render still walks all of `AGENTS`, drawing
   * a disabled row for the rest, so an agent that is merely not ready yet never
   * reads as one that was deleted. */
  const presentAgents = AGENTS.filter((a) => resolved.agentModels[a] !== undefined)

  /** One row's starting values — pre-fill from the doc tier when set, else
   * from the resolved-chain value. `dirty` starts false so a save without
   * edits sends `agentModels: {}` (no overrides written). Shared by the mount
   * seed and the late-hydration effect below so a row that appears mid-session
   * is filled exactly like one that was there from the start. */
  function seedRow(a: AdminAgentName, r: ResolvedAgentEntry): RowDraft {
    const docEntry = doc?.[a]
    // The resolved entry is a PARAMETER rather than looked up here, so a caller
    // cannot reach this function without one. That is what makes the partial
    // wire type load-bearing instead of decorative (#2137).
    // Pre-fill the namePattern dropdown: doc value first, else the
    // resolved-chain model spec (which is always populated — the chain
    // bottoms at the hardcoded floor).
    const docNamePattern = overrideToNamePattern(docEntry)
    // Pre-fill temperature: doc value first, else the resolved-chain
    // value. When neither is set we leave the input empty so the
    // placeholder ("(provider default)") shows.
    const initialTemp = docEntry?.temperature !== undefined
      ? String(docEntry.temperature)
      : r.temperature.value !== null
        ? String(r.temperature.value)
        : ''
    return {
      namePattern: docNamePattern || r.model.spec,
      temperature: initialTemp,
      dirty: false,
    }
  }

  const [draft, setDraft] = useState<Record<AdminAgentName, RowDraft>>(() => {
    const out = {} as Record<AdminAgentName, RowDraft>
    for (const a of presentAgents) {
      const r = resolved.agentModels[a]
      if (r) out[a] = seedRow(a, r)
    }
    return out
  })

  /** Fill in rows for agents that show up after mount.
   *
   * `presentAgents` is recomputed every render, so when a deployment finishes
   * and the backend starts returning an agent it did not have a moment ago,
   * that agent joins the render list. `draft` is seeded ONCE, so it has no
   * entry for it, and the render path would dereference undefined — the same
   * blank page one step later. Additive only: an agent already in `draft` is
   * left untouched, so a refetch can never clobber an edit in progress.
   *
   * The effect lands after the commit that first includes the new agent, so
   * that one frame renders with no row yet; the render skips it rather than
   * throwing. (Copilot review on #2137.) */
  useEffect(() => {
    const unseeded = presentAgents.filter((a) => draft[a] === undefined)
    if (unseeded.length === 0) return
    setDraft((d) => {
      const next = { ...d }
      for (const a of unseeded) {
        const r = resolved.agentModels[a]
        if (r && next[a] === undefined) next[a] = seedRow(a, r)
      }
      return next
    })
  })
  /** Re-seed every row the operator has not touched when the saved settings
   * change, as after a save. A row's draft holds its model, and an agent that
   * inherits one (the Deduper runs the Planner's, the Refiner the Proposer's)
   * changes when its parent is saved. Without this it would keep showing the
   * old model beside the new model's health, and write the old one back on its
   * next save. Rows with pending edits are left as they are. */
  const seededFrom = useRef(resolved)
  useEffect(() => {
    if (seededFrom.current === resolved) return
    seededFrom.current = resolved
    setDraft((d) => {
      const next = { ...d }
      for (const a of presentAgents) {
        const r = resolved.agentModels[a]
        if (r && next[a] && !next[a].dirty) next[a] = seedRow(a, r)
      }
      return next
    })
  }, [resolved])
  const [rowErrors, setRowErrors] = useState<Partial<Record<AdminAgentName, string>>>({})
  /** The agent whose Save is running (#2890). One at a time: a check and a
   * write per agent, so two at once could finish out of order. */
  const [savingAgent, setSavingAgent] = useState<AdminAgentName | null>(null)
  /** What each agent's last Save found, shown under its picker. */
  const [saveResults, setSaveResults] = useState<Partial<Record<AdminAgentName, SaveResult>>>({})
  /** Text typed in an agent's picker that named no single model when its
   * list closed (#2890). Save is held until a model is picked. */
  const [unresolvedTyping, setUnresolvedTyping] = useState<
    Partial<Record<AdminAgentName, { text: string; count: number }>>
  >({})

  /** Which agents are expanded (#2112). Everything starts closed; the seven
   * headers then fit together, which is the whole point. Nothing here is
   * persisted — reopening the page reopens nothing. */
  const [openAgents, setOpenAgents] = useState<ReadonlySet<AdminAgentName>>(() => new Set())

  /** A model sent here from the catalog's "Assign to" menu, and the model the
   * agent had before, for the "Picked from the model catalog" note. Cleared
   * once the change is saved or the operator picks something else. */
  const [fromCatalog, setFromCatalog] = useState<{ agent: AdminAgentName; model: string; was: string } | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  /** Take the catalog's `?agent=…&model=…` once: fill that agent's draft as an
   * unsaved change and open its row, then drop the parameters so a reload or
   * a later save does not apply them again. Nothing is saved here; the
   * operator still presses the agent's Save. An agent this page does not
   * know is ignored. */
  useEffect(() => {
    const agent = searchParams.get('agent')
    const model = searchParams.get('model')
    if (!agent || !model) return
    if (!(AGENTS as readonly string[]).includes(agent)) return
    const a = agent as AdminAgentName
    if (draft[a] === undefined) return
    const was = draft[a].namePattern
    if (was !== model) {
      setDraft((d) => ({ ...d, [a]: { ...d[a], namePattern: model, dirty: true } }))
      setFromCatalog({ agent: a, model, was })
    }
    setOpenAgents((prev) => (prev.has(a) ? prev : new Set([...prev, a])))
    const next = new URLSearchParams(searchParams)
    next.delete('agent')
    next.delete('model')
    setSearchParams(next, { replace: true })
  }, [searchParams, draft, setSearchParams])

  function toggleAgent(agent: AdminAgentName): void {
    setOpenAgents((prev) => {
      const next = new Set(prev)
      if (!next.delete(agent)) next.add(agent)
      return next
    })
  }

  function openAgentRows(agents: readonly AdminAgentName[]): void {
    setOpenAgents((prev) => {
      // Returning the SAME set when nothing changes is what lets React bail
      // out of the re-render. The auto-open effect below runs after every
      // render, so an updater that always minted a fresh Set — even one with
      // identical contents — would re-render, re-run the effect, and spin
      // forever.
      if (agents.every((a) => prev.has(a))) return prev
      const next = new Set(prev)
      for (const a of agents) next.add(a)
      return next
    })
  }

  /** A failing agent opens itself, so the page banner's promise — "See the fix
   * note under each row" — is still true with the rows closed. The note lives
   * inside the body, so a closed row would make the banner point at nothing.
   *
   * Fires on ENTRY into the failing set, not as a standing assertion. Verified
   * against how failures actually arrive: `checkAll` reloads verification six
   * times at 1500ms, so an effect that simply re-opened every failing agent on
   * each pass would fight a user who had closed one, reopening it every 1.5
   * seconds for nine seconds. Tracking the previous set means a hand-closed row
   * stays closed, and only a model that newly starts failing opens itself.
   *
   * A null `previous` is the first pass, where everything already failing is
   * "new" — that is the page-load case. */
  const previouslyFailing = useRef<ReadonlySet<AdminAgentName> | null>(null)
  useEffect(() => {
    const failing = new Set<AdminAgentName>()
    for (const a of presentAgents) {
      const targetId = agentTargetId(a)
      const t = statusByTarget.get(targetId)
      if (t && isNotWorkingWithFix(t, perRowChecking[targetId] ?? false)) failing.add(a)
    }
    const previous = previouslyFailing.current
    previouslyFailing.current = failing
    openAgentRows(presentAgents.filter((a) => failing.has(a) && !previous?.has(a)))
  })

  /** All available `<provider>:<model>` patterns from the pricing
   * catalog, sorted for stable display. Memoized so a re-render
   * doesn't reshuffle the dropdown. */
  // Only what this install can pick. A row the server marks unavailable (its
  // supplier has no key, the key was rejected, or the supplier stopped
  // listing it) is left out, the same rule the save and the run apply.
  const catalogPatterns = useMemo(
    () => pricingRows
      .filter((r) => r.available !== false)
      .map((r) => r.namePattern)
      .sort((a, b) => a.localeCompare(b)),
    [pricingRows],
  )

  /** Pricing rows keyed by namePattern — one lookup table so building each
   * option's facets is O(1) per option, not a `.find` over ~600 rows per
   * option (#1844 Slice 1.5). */
  const rowByPattern = useMemo(
    () => new Map(pricingRows.map((r) => [r.namePattern, r])),
    [pricingRows],
  )

  /** Provider keys present in the catalog, in alphabetical order — the
   * SearchableSelect renders header bands in this order. Patterns
   * without a colon ("bare model" rows in the catalog) fall through to
   * an `'other'` bucket so they're still visible. */
  const providerGroups = useMemo(() => {
    const seen = new Set<string>()
    for (const p of catalogPatterns) {
      const { provider } = parseNamePattern(p)
      seen.add(provider ?? 'other')
    }
    return [...seen].sort((a, b) => a.localeCompare(b)).map((key) => ({
      key,
      label: key,
    }))
  }, [catalogPatterns])

  /** Per-agent options list. If the agent's resolved model (or doc
   * value) isn't already in the catalog, we surface it as a synthetic
   * extra option at the top labeled `(current: not in the model catalog)`
   * so the operator isn't forced to switch models just because the
   * pricing catalog hasn't seen this spec yet. The orphan option has
   * no `group` so the SearchableSelect renders it ungrouped (above
   * the provider headers). */
  /** Capabilities the given agent requires, from the map the SERVER sends on
   * the app-settings response (#1928). Deliberately not a constant in this
   * file: this rule decides whether a run may spend money, and hand-copying a
   * correctness rule into a second place is the drift #1902 spent two slices
   * removing from the agent prompts.
   *
   * Empty when the server has not sent it (older backend, or still loading),
   * which makes the agent facet simply not apply rather than filtering
   * everything out. */
  function requiredCapsFor(a: AdminAgentName): readonly string[] {
    return capabilityRequirements?.[a]?.required ?? []
  }

  /** The agent's advisory bar, from the same served map.
   *
   * An older backend sends `required` but no `bar`. Falling back to a bar built
   * from `required` alone (no context floor) keeps the tool-calling chip and
   * warning it always had. Without this, saving a model known to lack tools
   * would still record the override below, but with no warning shown first.
   * Undefined only when the agent declares nothing at all. */
  function barFor(a: AdminAgentName): AgentModelBar | undefined {
    const served = capabilityRequirements?.[a]?.bar
    if (served) return served
    const required = requiredCapsFor(a)
    return required.length > 0 ? { minContextTokens: 0, needs: required } : undefined
  }

  /** Every way this model misses the agent's bar: known misses and unknowns,
   * which the warning words differently. Empty for a model the catalog has no
   * row for — that case is covered by the not-offered handling, not here. */
  function modelFallsShort(a: AdminAgentName, namePattern: string): BarShortfall[] {
    const row = rowByPattern.get(namePattern)
    if (!row) return []
    return barShortfalls(row, barFor(a))
  }

  /** Whether the catalog positively says this model can't use tools while the
   * agent's run-time gate requires them. The ONLY case that records a
   * tool-calling override on save: the other bar criteria only warn, and the
   * gate never reads them. */
  function toolCallingKnownMissing(a: AdminAgentName, namePattern: string): boolean {
    if (!requiredCapsFor(a).includes('toolCalling')) return false
    return rowByPattern.get(namePattern)?.toolCalling === false
  }

  /** Why a model can't be picked here, or null. `agent` lets the server's
   * answer for that agent's resolved model cover a retired model. */
  function unavailableReason(namePattern: string, agent?: AdminAgentName) {
    return modelUnavailableReason(
      namePattern,
      rowByPattern,
      suppliers,
      agent ? resolved.agentModels[agent]?.model : undefined,
    )
  }

  /** The suppliers the picker leaves out, for its footer line. */
  const missingSuppliersNote = useMemo(() => {
    const rejected = suppliers.filter((s) => s.state === 'keyRejected').map((s) => s.name)
    const noKey = suppliers.filter((s) => s.state === 'noKey').map((s) => s.name)
    const parts: string[] = []
    if (rejected.length) parts.push(`${listNames(rejected)} ${rejected.length === 1 ? 'had its key' : 'had their keys'} rejected`)
    if (noKey.length) parts.push(`${listNames(noKey)} ${noKey.length === 1 ? 'has' : 'have'} no key`)
    return parts.length ? `Not shown: ${parts.join(', and ')}.` : null
  }, [suppliers])

  /** Retired models by spec, for the picker's current-value label (#2014). */
  const retiredByPattern_ = useMemo(() => retiredByPattern(retiredModels), [retiredModels])

  /** Is this row pointing at something the catalog no longer offers? (#2107)
   *
   * The catalog is what decides selectability, so off-catalog means unusable
   * when the policy is on — including a model that is merely unknown rather
   * than excluded. The frontend cannot tell those apart once the row is
   * deleted: there is nothing left to read a country off. */
  function isOffCatalog(a: AdminAgentName): boolean {
    const current = draft[a].namePattern
    return current !== '' && !catalogPatterns.includes(current)
  }

  function optionsForAgent(a: AdminAgentName): readonly SearchableSelectOption[] {
    const current = draft[a].namePattern
    // #2107 — no orphan fallback under the policy. Normally this option exists
    // so nobody is forced to switch models just because the catalog has not
    // caught up; here it would keep an agent on a model the deployment is not
    // supposed to have.
    const orphan = !originPolicyActive && isOffCatalog(a)
    const orphanOpt: SearchableSelectOption | null = orphan
      // #2014 — say WHICH kind of missing. A retired model is one an operator
      // could pick yesterday; a never-seen spec is a different situation and
      // keeps the original wording.
      ? {
          value: current,
          label: unavailableReason(current, a)
            ? `${current} (current: no longer available)`
            : `${current} (current: ${retiredOptionSuffix(retiredByPattern_.get(current))})`,
        }
      : null
    const catalogOpts: SearchableSelectOption[] = catalogPatterns.map((p) => {
      const { provider } = parseNamePattern(p)
      const row = rowByPattern.get(p)
      // #1844 Slice 1.5 — facet membership per option (curated/frontier from
      // the row's collections overlay, fits-agent from the agent bar). The
      // orphan option below stays facet-exempt (no `facets`) so a current
      // model missing from the catalog is never hidden behind a facet.
      // #1844 Slice 2 — the relative cost pill rides along as the option's
      // trailing adornment, so the operator compares cost WHILE browsing the
      // list rather than having to pick a model first to see its cost. An
      // unpriced row carries no category → no pill.
      const cat = row?.costCategory
      return {
        value: p,
        label: p,
        group: provider ?? 'other',
        facets: optionFacets(row, barFor(a)),
        // The row lays these out on their own line and handles wrapping, so
        // this is just the badge set — no alignment or spacing of its own.
        trailing: (
          <span className="flex items-center gap-1 flex-wrap">
            <CapabilityBadge value={row?.toolCalling} label="🔧 tool calling" />
            {reasoningBadge(row)}
            {formatContextWindow(row?.maxInputTokens) && (
              <Badge variant="secondary" className="w-fit text-[10px]">
                {formatContextWindow(row?.maxInputTokens)}
              </Badge>
            )}
            {cat ? <CostCategoryPill category={cat} /> : null}
          </span>
        ),
      }
    })
    return orphanOpt ? [orphanOpt, ...catalogOpts] : catalogOpts
  }

  /** The agent whose model this one runs when it has none of its own: the
   * Refiner runs the Proposer's and the Deduper the Planner's. Each reads only
   * its own page setting and its own settings-file line before inheriting, so
   * a model from a settings-file line could be either its own line or its
   * parent's. That case is not called inherited, even when the names match;
   * it reads "from the install's settings file", which is true either way. */
  function inheritsFrom(a: AdminAgentName): AdminAgentName | null {
    const parent: AdminAgentName | null = a === 'refiner' ? 'proposer' : a === 'dedupJudge' ? 'planner' : null
    if (!parent || doc?.[a]?.model) return null
    const own = resolved.agentModels[a]?.model
    if (!own || own.source === 'per-agent-env') return null
    return own.spec === resolved.agentModels[parent]?.model.spec ? parent : null
  }

  /** Parse a temperature draft into a number, `null` (empty/cleared),
   * or a string error. Mirrors the server-side rule: undefined OR
   * finite number in [0, 2]. */
  function parseTempDraft(raw: string): number | null | string {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return 'must be a finite number in [0, 2]'
    if (n < 0 || n > 2) return 'must be in [0, 2]'
    return n
  }

  function setSaveResult(a: AdminAgentName, result: SaveResult | null): void {
    setSaveResults((prev) => {
      const next = { ...prev }
      if (result) next[a] = result
      else delete next[a]
      return next
    })
  }

  /** The picker closed with text typed (#2890). Text that named one model
   * was already picked by the picker; anything else holds Save and says
   * why, since the press that closed the list may have been on Save. */
  function onTypingLeft(a: AdminAgentName, text: string, count: number): void {
    if (text && count !== 1) {
      setUnresolvedTyping((prev) => ({ ...prev, [a]: { text, count } }))
      setSaveResult(a, {
        tone: 'info',
        text:
          count === 0
            ? `"${text}" matches no model. Pick one from the list.`
            : `"${text}" matches ${count} models. Pick one from the list.`,
      })
      return
    }
    setUnresolvedTyping((prev) => {
      if (!(a in prev)) return prev
      const next = { ...prev }
      delete next[a]
      return next
    })
    setSaveResults((prev) => {
      if (prev[a]?.tone !== 'info') return prev
      const next = { ...prev }
      delete next[a]
      return next
    })
  }

  /**
   * One agent's Save (#2890): check the model first, then save only this
   * agent, then check whatever the save changed, inherited agents included.
   *
   * A model that fails its check is not saved. One whose supplier could not
   * be reached (`unreachable`: a network failure or the check's time limit)
   * is saved, and its status reads "Not checked yet" because the follow-up
   * check is skipped. Going back to the install's default checks the model
   * the agent would fall back to. A temperature-only change checks nothing,
   * since the check never sends a temperature.
   *
   * Only this agent is sent, and the server stores each agent's entry on its
   * own (`agentModels.<agent>`), so another agent's unsaved change stays
   * unsaved.
   */
  async function saveAgent(a: AdminAgentName): Promise<void> {
    const row = draft[a]
    if (!row || !resolved.agentModels[a] || savingAgent !== null || unresolvedTyping[a]) return
    if (!row.dirty) {
      setSaveResult(a, { tone: 'info', text: 'Nothing has changed. Pick a model or change the temperature first.' })
      return
    }
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[a]
      return next
    })
    setSaveResult(a, null)

    // #2107 — hiding the option is not enough. The draft still holds the old
    // spec, so editing only the temperature would write the removed model
    // straight back. Refuse instead: it says "cannot be used" out loud rather
    // than changing the configuration behind the user.
    if (originPolicyActive && isOffCatalog(a)) {
      setRowErrors((prev) => ({ ...prev, [a]: 'this model is no longer available — pick one from the list before saving' }))
      return
    }
    // Temperature parsing + client-side validation. Empty input means
    // clear-the-override.
    const tempParsed = parseTempDraft(row.temperature)
    if (typeof tempParsed === 'string') {
      setRowErrors((prev) => ({ ...prev, [a]: `temperature ${tempParsed}` }))
      return
    }
    await checkAndSave(a, row.namePattern.trim(), tempParsed)
  }

  /** The settings this section was last drawn with. Apply saves several
   * agents in a row, and each save reloads them, so the steps after the
   * first read the latest values here rather than the ones Apply started
   * with. */
  const latest = useRef({ resolved, doc })
  latest.current = { resolved, doc }
  /** True while a check-and-write runs. A ref, not state, so the agent's
   * Save and Apply cannot both start one in the same moment: state would
   * not show the first until the next render. */
  const saveInFlight = useRef(false)

  /**
   * The check and the write behind an agent's Save, shared with Apply
   * (#2891) so a recommendation is checked and saved exactly as a model
   * picked by hand. Saves `np` (or the install's default when empty) with
   * `temperature` as this agent's whole entry. Shows its progress and result
   * under the agent's picker, and returns that result. Never throws: a
   * failed write is shown as the row's error and returned as `bad`.
   */
  async function checkAndSave(a: AdminAgentName, np: string, temperature: number | null): Promise<SaveResult> {
    const { resolved, doc } = latest.current
    const r = resolved.agentModels[a]
    if (!r) return { tone: 'bad', text: 'Not saved. This agent is not ready yet.' }
    const override: AgentModelOverride = {}
    if (np) {
      const { provider, model } = parseNamePattern(np)
      if (provider) override.provider = provider
      override.model = model
    }
    if (temperature !== null) override.temperature = temperature

    // A cleared picker drops the model picked here, so the agent goes back
    // to this install's default: an entry with no model does that, since the
    // server sets each sent agent's entry whole.
    const cleared = np === ''
    const modelChanged = cleared ? Boolean(doc?.[a]?.model) : !sameModel(np, r.model.spec)

    if (saveInFlight.current) return { tone: 'bad', text: 'Not saved. Another save is still running. Try again when it finishes.' }
    saveInFlight.current = true
    setSavingAgent(a)
    try {
      let check: ModelCandidateCheck | null = null
      if (modelChanged) {
        setSaveResult(a, { tone: 'info', text: 'Checking the model before saving it.' })
        check = await adminModelVerificationAPI.check(a, cleared ? null : np)
        if (check.outcome === 'fails') {
          const refused: SaveResult = { tone: 'bad', text: `Not saved. ${check.fixMessage}` }
          setSaveResult(a, refused)
          return refused
        }
      }

      // #1928 — saving a model the catalog reports as unable to meet the
      // agent's requirement RECORDS the operator's assertion, rather than just
      // warning and moving on. Without this the runtime gate (#1927) would
      // refuse the very model they just insisted on. Written per model row:
      // capability is a property of the model. Not swallowed: if recording it
      // fails, the operator must know the model was not saved.
      if (np && toolCallingKnownMissing(a, np)) {
        const catalogRow = rowByPattern.get(np)
        if (catalogRow?._id) await adminPricingAPI.setCapabilityOverride(catalogRow._id, 'toolCalling', true)
      }

      // Each agent's effective model before the write, so the page can check
      // the ones the save changed, inherited ones included.
      const before: Partial<Record<AdminAgentName, string>> = {}
      for (const agent of presentAgents) {
        const spec = resolved.agentModels[agent]?.model.spec
        if (spec !== undefined) before[agent] = spec
      }
      await adminAppSettingsAPI.update({ agentModels: { [a]: override } })
      // Only this agent is saved, so only its flag clears. The reload below
      // re-seeds clean drafts from the stored settings and leaves dirty ones.
      // The model is set too, for Apply, whose model was never in the draft.
      setDraft((d) => ({ ...d, [a]: { ...d[a], namePattern: np, dirty: false } }))
      if (fromCatalog?.agent === a) setFromCatalog(null)
      const label = AGENT_LABEL[a].toLowerCase()
      const supplierName = (spec: string) => supplierNameFor(parseNamePattern(spec).provider, suppliers)
      const result: SaveResult = !modelChanged
        ? { tone: 'ok', text: "Saved. The model wasn't changed, so it wasn't checked again.", saved: true }
        : check?.outcome === 'unreachable'
          ? { tone: 'warn', text: `Saved, but ${supplierName(check.model)} couldn't be reached to check it. Press Check now later.`, saved: true }
          : cleared
            ? { tone: 'ok', text: `Saved. The ${label} now uses this install's default model.`, saved: true }
            : { tone: 'ok', text: `Saved. The ${label} will use this model on its next run.`, saved: true, checkedWorking: check?.outcome === 'works' }
      setSaveResult(a, result)
      await onSaved(before, { checkChanged: check?.outcome !== 'unreachable' })
      return result
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Save failed'
      setSaveResult(a, null)
      setRowErrors((prev) => ({ ...prev, [a]: message }))
      return { tone: 'bad', text: `Not saved. ${message}` }
    } finally {
      saveInFlight.current = false
      setSavingAgent(null)
    }
  }

  /** The review list, while open (#2891). `recoAsk` counts presses, so each
   * press starts a fresh list with its own ticks and results. */
  const [recoView, setRecoView] = useState<RecommendationsView | null>(null)
  const [recoAsk, setRecoAsk] = useState(0)
  /** Apply is working through its ticks; a new press would abandon them. */
  const [recoApplying, setRecoApplying] = useState(false)
  /** Counts asks and closes. An answer that comes back after the list was
   * closed, or after a newer ask, no longer matches and is dropped, so it
   * can neither reopen the list nor replace a newer one. */
  const recoRequest = useRef(0)
  /** OttoCloud is a supplier here, so this install is an OttoCloud customer
   * and may ask. The server applies the same rule. */
  const ottoCloudSetUp = suppliers.some((s) => s.supplier === HOSTED_SUPPLIER && s.state !== 'noKey')

  /** Ask OttoCloud once, on the press, and fix each agent's line at the
   * moment of asking. */
  async function showRecommendations(): Promise<void> {
    const ask = ++recoRequest.current
    setRecoAsk((n) => n + 1)
    setRecoView({ phase: 'asking' })
    let answer: ModelRecommendationsResponse
    try {
      answer = await adminModelRecommendationsAPI.get(await currentOrgId())
    } catch (e) {
      if (ask !== recoRequest.current) return
      setRecoView({
        phase: 'message',
        text: `Couldn't get recommendations: ${e instanceof Error ? e.message : 'the request failed'}. Nothing was changed.`,
      })
      return
    }
    if (ask !== recoRequest.current) return
    if (answer.kind !== 'answered') {
      setRecoView({
        phase: 'message',
        text:
          answer.kind === 'noAccount'
            ? "This organization has no OttoCloud account linked, so OttoCloud can't recommend models for it. Nothing was changed."
            : answer.kind === 'notSetUp'
              ? "OttoCloud isn't set up on this install, so there are no recommendations. Nothing was changed."
              : "OttoCloud didn't answer, so there are no recommendations right now. Nothing was changed. Try again in a moment.",
      })
      return
    }
    const items: RecommendationItem[] = []
    for (const agent of presentAgents) {
      const now = resolved.agentModels[agent]?.model.spec
      const rec = answer.recommendations[agent]
      if (now === undefined || !rec) continue
      const target = statusByTarget.get(agentTargetId(agent))
      items.push({
        agent,
        now,
        nowNotWorking: target ? effectiveModelState(target, perRowChecking[target.targetId] ?? false) === 'notWorking' : false,
        rec,
        alreadyRecommended: rec.status === 'recommended' && sameModel(rec.model, now),
      })
    }
    setRecoView({ phase: 'ready', items })
  }

  /** One ticked agent's Apply: its own Save with the recommended model and
   * the temperature already saved for it, so applying changes the model
   * only. The list words a success plainly; anything else reads as the row
   * would. */
  async function applyRecommendation(a: AdminAgentName, model: string): Promise<ApplyResult> {
    // A pick, like one from the list: it settles any typing held in the
    // agent's picker, which would otherwise keep its Save off.
    onTypingLeft(a, '', 0)
    const saved = latest.current.doc?.[a]?.temperature
    const result = await checkAndSave(a, model, typeof saved === 'number' ? saved : null)
    const written = result.saved === true
    if (result.checkedWorking) return { tone: 'ok', text: 'Saved and working.', saved: written }
    return { tone: result.tone === 'info' ? 'ok' : result.tone, text: result.text, saved: written }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
        <CardTitle>Agent models</CardTitle>
        {/* OttoCloud customers only: without OttoCloud there is no button
            and no recommendation anywhere on the screen. */}
        {ottoCloudSetUp && (
          <Button
            type="button"
            variant="outline"
            onClick={() => void showRecommendations()}
            disabled={recoView?.phase === 'asking' || recoApplying || savingAgent !== null}
          >
            <span aria-hidden="true" className="mr-1.5">★</span>
            Show recommendations
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4" aria-label="agent-models">
          <p className="text-xs text-muted-foreground">
            Open an agent, pick its model, and press <b>Save</b> beside it. Click a model in the list, or type its
            name: once your typing matches one model, Save uses it.
          </p>
          {recoView && (
            <RecommendationsList
              key={recoAsk}
              view={recoView}
              pricingRows={pricingRows}
              onApply={applyRecommendation}
              busy={savingAgent !== null && !recoApplying}
              onRunningChange={setRecoApplying}
              onClose={() => {
                recoRequest.current++
                setRecoView(null)
              }}
            />
          )}
          {/* Own container so the gap between agents is independent of the
              form's `space-y-4` — closed rows read as a list at 8px, where
              16px made seven headers look like seven unrelated cards. */}
          <div className="space-y-2">
          {AGENTS.map((agent) => {
            const r = resolved.agentModels[agent]
            const row = draft[agent]
            // Two ways a row has nothing to draw, and they want the same
            // treatment. `!r` is the deployment window: this backend has never
            // heard of the agent. `!row` is the single frame after one appears,
            // before the hydration effect above has seeded its draft. Rendering
            // the disabled row covers both without a second branch.
            if (!r || !row) return <UnavailableAgentRow key={agent} agent={agent} />
            const options = optionsForAgent(agent)
            const rowError = rowErrors[agent]
            const targetId = agentTargetId(agent)
            const isOpen = openAgents.has(agent)
            const bodyId = `${agent}-settings`
            // The picker was cleared: the save drops the model picked here and
            // the agent goes back to this install's default. Which model that
            // is gets decided on the server, so it is not guessed at here.
            const cleared = row.dirty && row.namePattern === ''
            // The same expression everywhere below, so the summary and the
            // row it summarises can never name different models. Empty when
            // cleared, so nothing below describes the model being dropped.
            const spec = cleared ? '' : row.namePattern || r.model.spec
            // A model change that is not saved yet. Health and Check now
            // describe the SAVED model, so they wait for the save. Clearing an
            // agent that has no model picked here changes nothing.
            const unsavedModel = cleared
              ? Boolean(doc?.[agent]?.model)
              : row.dirty && !sameModel(spec, r.model.spec)
            const capRow = rowByPattern.get(spec)
            const parent = inheritsFrom(agent)
            const priceStatus = modelPriceStatus(spec, pricingRows)
            const cost = modelCostCategory(spec, pricingRows)
            return (
              <div key={agent} className="border rounded-lg">
                {/* The copy button sits beside the header, not inside it: the
                    header is itself a button, and a button cannot hold one.

                    Only the header is trimmed to the rounded corners. Trimming
                    the whole row cut off the model list, which opens below the
                    field and is taller than the rest of the row. The radius is
                    one pixel under the row's so the shade meets the border. */}
                <div
                  className={`flex items-center overflow-hidden rounded-t-[calc(var(--radius)-1px)] ${
                    isOpen ? 'bg-muted' : 'bg-muted/60 rounded-b-[calc(var(--radius)-1px)]'
                  }`}
                >
                <div className="flex-1 min-w-0">
                <AgentRowHeader
                  agent={agent}
                  open={isOpen}
                  bodyId={bodyId}
                  modelSpec={cleared ? "this install's default" : spec}
                  costCategory={cost}
                  unavailable={unavailableReason(spec, agent) !== null}
                  dirty={row.dirty}
                  unsavedModel={unsavedModel}
                  target={statusByTarget.get(targetId)}
                  checking={perRowChecking[targetId] ?? false}
                  onToggle={() => toggleAgent(agent)}
                />
                </div>
                <CopyIconButton
                  value={spec}
                  title="Copy model name"
                  ariaLabel={`Copy the ${AGENT_LABEL[agent]}'s model name`}
                  className="mr-2"
                />
                </div>
                {isOpen && (
              <div id={bodyId} className="border-t p-3 space-y-3">
                {/* What is wrong and what to do comes first. Hidden until the
                    verification list loads (or if it soft-failed), so an outage
                    never hides the controls below. */}
                <ModelHealth
                  target={statusByTarget.get(targetId)}
                  checking={perRowChecking[targetId] ?? false}
                  error={perRowError[targetId]}
                  onCheck={() => onCheckRow(targetId)}
                  unsavedModel={unsavedModel}
                />
                <div className="space-y-1">
                  <Label htmlFor={`${agent}-namePattern`} className="text-xs text-muted-foreground">
                    Model
                  </Label>
                  <div className="flex flex-wrap items-start gap-2">
                  <div className="flex-1 min-w-[16rem]">
                  {/* Searchable combobox — typing narrows the catalog
                      (provider + model substring), a click picks. No Enter
                      needed (#2890): leaving the list with text that names one
                      model picks it, so the Save beside it uses what was typed.
                      The CLEAR_OPTION_VALUE sentinel is prepended as a
                      synthetic ungrouped option: choosing it and saving drops
                      the model picked here, so the agent goes back to this
                      install's default. */}
                  <SearchableSelect
                    id={`${agent}-namePattern`}
                    aria-label={`${agent} model`}
                    value={row.namePattern}
                    onChange={(next) => {
                      setDraft((d) => ({
                        ...d,
                        [agent]: { ...d[agent], namePattern: next, dirty: true },
                      }))
                      setSaveResult(agent, null)
                    }}
                    options={[
                      { value: CLEAR_OPTION_VALUE, label: "— Use this install's default" },
                      ...options,
                    ]}
                    groups={providerGroups}
                    facets={modelFacetsForAgent(agent, barFor(agent))}
                    defaultFacet={FACET_CURATED}
                    placeholder="Select a model"
                    searchPlaceholder="Search models…"
                    disabled={savingAgent === agent}
                    footer={missingSuppliersNote}
                    className={unavailableReason(spec, agent) ? 'border-destructive' : ''}
                    pickSoleMatch
                    onSearchLeft={(text, count) => onTypingLeft(agent, text, count)}
                    searchHint={(_typed, sole) =>
                      sole ? 'Matches one model. Save uses it; no need to press Enter.' : null
                    }
                  />
                  </div>
                  <Button
                    type="button"
                    onClick={() => void saveAgent(agent)}
                    disabled={savingAgent !== null || recoApplying || unresolvedTyping[agent] !== undefined}
                    data-testid={`${agent}-save`}
                  >
                    {savingAgent === agent ? 'Saving…' : 'Save'}
                  </Button>
                  </div>
                  {saveResults[agent] && (
                    <p
                      className={`rounded-md px-2 py-1.5 text-xs ${SAVE_RESULT_STYLE[saveResults[agent]!.tone]}`}
                      role="status"
                      data-testid={`${agent}-save-result`}
                    >
                      {saveResults[agent]!.text}
                    </p>
                  )}
                </div>
                {fromCatalog?.agent === agent && row.dirty && row.namePattern === fromCatalog.model && (
                  <div
                    className="rounded-r-md border-l-4 border-success bg-success/10 px-2 py-1.5 text-xs"
                    data-testid={`${agent}-from-catalog`}
                  >
                    Picked from the model catalog. It was{' '}
                    <span className="font-mono">{fromCatalog.was || 'the default'}</span>. Press{' '}
                    <b>Save</b> beside it to keep it.
                  </div>
                )}
                {(() => {
                  const reason = unavailableReason(spec, agent)
                  if (!reason) return null
                  const name = supplierNameFor(parseNamePattern(spec).provider, suppliers)
                  return (
                    <div className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs" role="alert">
                      <b>No longer available. Pick another model.</b>{' '}
                      {unavailableReasonText(reason, name)} The {AGENT_LABEL[agent].toLowerCase()} won't run until you
                      pick one.
                    </div>
                  )
                })()}
                {/* A missing price stays in the open: an unpriced model costs
                    $0 in the cost rollup until its rates are filled in. */}
                {priceStatus === 'unpriced' && (
                  <Badge variant="warning" className="w-fit text-[10px]">
                    ⚠ unpriced — see Pricing page
                  </Badge>
                )}
                {/* Save-time warning. Reached by searching past the filter,
                    which SearchableSelect deliberately allows — so the chip
                    can never be the only guard. A warning, not a block:
                    capability data can be missing or stale, and locking an
                    operator out of a model that actually works is worse than
                    letting them proceed informed. */}
                {(() => {
                  // One line per way the model misses this agent's bar.
                  // Known misses are the warning; unknowns are a softer
                  // note, because "we don't know" is not "it can't".
                  const bar = barFor(agent)
                  const misses = modelFallsShort(agent, spec)
                  if (!bar || misses.length === 0) return null
                  const known = misses.filter((m) => m.known)
                  const unknown = misses.filter((m) => !m.known)
                  const ctx = capRow?.maxInputTokens
                  const toolsMissing = known.some((m) => m.criterion === 'toolCalling')
                  return (
                    <>
                      {known.length > 0 && (
                        <div className="rounded-md border border-warning bg-warning/10 p-2 text-xs space-y-1" data-testid={`${agent}-bar-warning`}>
                          {known.map((m) => (
                            <div key={m.criterion}>⚠ <b>{shortfallLine(agent, m, bar, ctx)}</b></div>
                          ))}
                          <div>
                            {toolsMissing
                              ? `The ${AGENT_LABEL[agent].toLowerCase()} will be skipped and your run will keep going, with a note in the run log. `
                              : ''}
                            You can still save this if you know our records are out of date.
                          </div>
                        </div>
                      )}
                      {unknown.length > 0 && (
                        <div className="text-xs text-muted-foreground space-y-0.5" data-testid={`${agent}-bar-unknown`}>
                          {unknown.map((m) => (
                            <div key={m.criterion}>{shortfallLine(agent, m, bar, ctx)}</div>
                          ))}
                        </div>
                      )}
                    </>
                  )
                })()}
                {rowError && (
                  <p className="text-xs text-destructive" role="alert">
                    {rowError}
                  </p>
                )}
                {/* Closed when the row opens. A rejected temperature still
                    shows its error above, so a closed section never hides it. */}
                <details className="group border-t border-dashed pt-2" data-testid={`${agent}-more-details`}>
                  <summary className="cursor-pointer select-none list-none inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
                    <ChevronRight aria-hidden="true" className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
                    More details
                  </summary>
                  <div className="mt-3 space-y-2">
                    <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm items-baseline">
                      <dt>
                        <Label htmlFor={`${agent}-temperature`} className="text-sm font-normal text-muted-foreground">
                          Temperature
                        </Label>
                      </dt>
                      <dd className="min-w-0 space-y-1">
                        {/*
                          type="text" inputMode="decimal" rather than
                          type="number": we want to validate ourselves
                          (out-of-range, NaN, empty) so the error path is
                          explicit; type="number" lets browsers silently
                          drop characters and clamp values, which would
                          mask the operator's typo instead of surfacing it.
                        */}
                        <Input
                          id={`${agent}-temperature`}
                          type="text"
                          inputMode="decimal"
                          value={row.temperature}
                          placeholder="This agent's default"
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              [agent]: { ...d[agent], temperature: e.target.value, dirty: true },
                            }))
                          }
                          disabled={savingAgent === agent}
                          className="max-w-[220px]"
                          aria-invalid={rowError ? 'true' : undefined}
                        />
                        <p className="text-xs text-muted-foreground">
                          Leave empty to use this agent's default. Most agents work best that way.
                        </p>
                      </dd>
                      <dt className="text-muted-foreground">Set where</dt>
                      <dd className="min-w-0" data-testid={`${agent}-set-where`}>
                        Model: {parent ? `the same as the ${AGENT_LABEL[parent]}'s` : modelSourceWords(r.model.source)}.
                        {' '}Temperature: {temperatureSourceWords(r.temperature.source)}.
                      </dd>
                      <dt className="text-muted-foreground">This model</dt>
                      <dd className="min-w-0 flex flex-wrap items-center gap-1">
                        {/* The shared capability renderers, so this and the
                            Pricing page describe a model the same way. */}
                        {priceStatus === 'priced' && (
                          <Badge variant="secondary" className="w-fit text-[10px]">✓ priced</Badge>
                        )}
                        {modelReadsImages(spec, pricingRows) && (
                          <Badge variant="secondary" className="w-fit text-[10px]">👁 vision enabled</Badge>
                        )}
                        <CapabilityBadge value={capRow?.toolCalling} label="🔧 tool calling" />
                        {reasoningBadge(capRow)}
                        {formatContextWindow(capRow?.maxInputTokens) && (
                          <Badge variant="secondary" className="w-fit text-[10px]">
                            {formatContextWindow(capRow?.maxInputTokens)}
                          </Badge>
                        )}
                        {cost && <CostCategoryPill category={cost} />}
                      </dd>
                    </dl>
                    {/* Masked preview of the key this agent's supplier uses —
                        confirms which key is configured after a rotation. */}
                    <ApiKeyPreview target={statusByTarget.get(targetId)} />
                  </div>
                </details>
              </div>
                )}
              </div>
            )
          })}
          </div>

        </div>
      </CardContent>
    </Card>
  )
}

export default AgentModelsPage

/** "A", "A and B", "A, B and C". */
function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}
